import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { BusinessUnitsService } from 'src/app/services/business-units.service';
import { BusinessUnit } from '@models/business-unit.interface';
import { SaveBusinessUnitDto } from '@models/save-business-unit-dto.interface';
import { LookupDto } from '@models/lookup-dto.interface';

/** Emitted when a save completes so the parent can refresh and re-select. */
export interface BusinessUnitSavedEvent {
  id: number;
}

@Component({
  selector: 'app-business-unit-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './business-unit-detail.component.html',
  styleUrl: './business-unit-detail.component.scss'
})
export class BusinessUnitDetailComponent implements OnChanges, OnDestroy {
  @Input() businessUnit: BusinessUnit | null = null;

  @Output() saved = new EventEmitter<BusinessUnitSavedEvent>();

  private readonly businessUnitsService = inject(BusinessUnitsService);
  private readonly fb = inject(FormBuilder);
  private saveRequest?: Subscription;

  protected isSaving = signal(false);
  protected errorMessage = signal('');
  protected assetClasses = signal<LookupDto[]>([]);

  protected form = this.fb.group({
    name:  [null as string | null, [Validators.required, Validators.maxLength(200)]],
    alias: [null as string | null, Validators.maxLength(50)],
    assetClassId: [null as number | null],
    applyDeepwaterFormat: [false]
  });

  constructor() {
    this.businessUnitsService.getAssetClasses().subscribe({
      next: assetClasses => this.assetClasses.set(assetClasses),
      error: () => this.assetClasses.set([])
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['businessUnit']) return;

    if (this.businessUnit) {
      this.form.reset();
      this.form.patchValue({
        name:  this.businessUnit.name ?? '',
        alias: this.businessUnit.alias,
        assetClassId: this.businessUnit.assetClassId ?? null,
        applyDeepwaterFormat: this.businessUnit.applyDeepwaterFormat ?? false
      }, { emitEvent: false });
    } else {
      this.form.reset();
    }

    this.errorMessage.set('');
    this.form.enable();
  }

  ngOnDestroy(): void {
    this.saveRequest?.unsubscribe();
  }

  get isNew(): boolean {
    return !this.businessUnit || this.businessUnit.id === 0;
  }

  protected focusNextInput(event: KeyboardEvent): void {
    const form = event.currentTarget as HTMLFormElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableControls = Array.from(form.querySelectorAll<HTMLElement>(
      'input[formControlName]:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]), select[formControlName]:not([disabled])'
    ));
    const currentIndex = editableControls.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableControls.length - 1) {
      editableControls[currentIndex + 1].focus();
    }
  }

  protected onSave(): void {
    if (this.form.invalid || this.isSaving()) return;

    const raw = this.form.getRawValue();
    const dto: SaveBusinessUnitDto = {
      name:  raw.name!.trim(),
      alias: raw.alias?.trim() ?? null,
      assetClassId: raw.assetClassId != null && String(raw.assetClassId) !== '' ? Number(raw.assetClassId) : null,
      applyDeepwaterFormat: raw.applyDeepwaterFormat ?? false
    };

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.clearServerErrors();

    const request$ = this.isNew
      ? this.businessUnitsService.create(dto)
      : this.businessUnitsService.update(this.businessUnit!.id, dto);

    this.saveRequest = request$.subscribe({
      next: savedBusinessUnit => {
        this.isSaving.set(false);
        this.saved.emit({ id: savedBusinessUnit.id });
      },
      error: (err: HttpErrorResponse) => {
        this.isSaving.set(false);
        this.handleServerError(err);
      }
    });
  }

  private handleServerError(err: HttpErrorResponse): void {
    const body = err.error;

    // Map field-level errors from ValidationProblem response onto form controls.
    if (body?.errors && typeof body.errors === 'object') {
      const errors = body.errors as Record<string, string[]>;

      let mapped = false;
      for (const [serverField, messages] of Object.entries(errors)) {
        const controlName = Object.keys(this.form.controls)
          .find(key => key.toLowerCase() === serverField.toLowerCase());
        const control = controlName ? this.form.get(controlName) : null;
        if (control) {
          control.setErrors({ serverError: messages[0] });
          control.markAsTouched();
          mapped = true;
        }
      }

      if (!mapped) {
        this.errorMessage.set('Save failed. Please check your entries and try again.');
      }
    } else {
      this.errorMessage.set('An unexpected error occurred. Please try again.');
    }
  }

  private clearServerErrors(): void {
    for (const key of Object.keys(this.form.controls)) {
      const control = this.form.get(key);
      if (control?.hasError('serverError')) {
        const { serverError: _, ...rest } = control.errors ?? {};
        control.setErrors(Object.keys(rest).length > 0 ? rest : null);
      }
    }
  }
}
